import { Component, OnInit } from '@angular/core';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface SantanderLinha {
  dataIso: string;
  dataExibicao: string;
  codCentroCusto: string;
  centroCusto: string;
  codPlanoContas: string;
  descricaoPlanoContas: string;
  credito: number;
  debito: number;
  saldo: number;
  informacoesAdicionais: string;
  observacao: string;
  mesPagamento: number;
}

type CampoOrdenacaoSantander =
  | 'dataIso'
  | 'codCentroCusto'
  | 'centroCusto'
  | 'codPlanoContas'
  | 'descricaoPlanoContas'
  | 'credito'
  | 'debito'
  | 'saldo'
  | 'informacoesAdicionais'
  | 'observacao'
  | 'mesPagamento';

@Component({
  selector: 'app-banco-santander',
  templateUrl: './banco-santander.component.html',
  styleUrls: ['./banco-santander.component.scss']
})
export class BancoSantanderComponent implements OnInit {
  ano = new Date().getFullYear();
  saldoInicialPeriodo: number | null = null;
  saldoInicialApi = 0;
  saldoAtual = 0;
  totalCreditos = 0;
  totalDebitos = 0;
  linhas: SantanderLinha[] = [];
  linhasOrdenadas: SantanderLinha[] = [];
  linhasPaginadas: SantanderLinha[] = [];
  loading = false;
  private linhasApi: any[] = [];

  paginaAtual = 1;
  itensPorPagina = 25;
  totalPaginas = 0;

  ordenacaoCampo: CampoOrdenacaoSantander = 'dataIso';
  ordenacaoDirecao: 'asc' | 'desc' = 'asc';

  private readonly camposNumericos: CampoOrdenacaoSantander[] = [
    'credito',
    'debito',
    'saldo',
    'mesPagamento'
  ];

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.carregarRelatorio();
  }

  alterarAno(delta: number): void {
    this.ano += delta;
    this.saldoInicialPeriodo = null;
    this.carregarRelatorio();
  }

  carregarRelatorio(): void {
    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(`${API}financeiro/ImportacaoExtratoBanco/RelatorioSantander?ano=${this.ano}`).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.linhas = [];
          this.linhasOrdenadas = [];
          this.linhasPaginadas = [];
          this.totalPaginas = 0;
          this.util.toasterShowError('Erro', response.message || 'Nao foi possivel carregar o extrato Santander.');
          return;
        }
        const data = this.desempacotar(response);
        this.saldoInicialApi = Number(data.saldoinicial ?? data.saldoInicial ?? 0);
        if (this.saldoInicialPeriodo == null) {
          this.saldoInicialPeriodo = this.saldoInicialApi;
        }
        this.linhasApi = Array.isArray(data.linhas) ? data.linhas : [];
        this.montarLinhas(this.linhasApi);
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.linhas = [];
        this.linhasOrdenadas = [];
        this.linhasPaginadas = [];
        this.totalPaginas = 0;
        this.util.toasterShowError('Erro', 'Nao foi possivel carregar o extrato Santander.');
      }
    });
  }

  recalcularSaldo(): void {
    this.montarLinhas(this.linhasApi);
  }

  ordenarPor(campo: CampoOrdenacaoSantander): void {
    if (this.ordenacaoCampo === campo) {
      this.ordenacaoDirecao = this.ordenacaoDirecao === 'asc' ? 'desc' : 'asc';
    } else {
      this.ordenacaoCampo = campo;
      this.ordenacaoDirecao = 'asc';
    }
    this.paginaAtual = 1;
    this.aplicarOrdenacaoEPaginacao();
  }

  iconeOrdenacao(campo: CampoOrdenacaoSantander): string {
    if (this.ordenacaoCampo !== campo) return 'fa-sort';
    return this.ordenacaoDirecao === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  paginaAnterior(): void {
    if (this.paginaAtual <= 1) return;
    this.paginaAtual--;
    this.atualizarPaginacao();
  }

  proximaPagina(): void {
    if (this.paginaAtual >= this.totalPaginas) return;
    this.paginaAtual++;
    this.atualizarPaginacao();
  }

  formatMoney(valor: number): string {
    return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatValorOuTraco(valor: number): string {
    const v = Number(valor || 0);
    if (v === 0) return '-';
    return `R$ ${this.formatMoney(v)}`;
  }

  private montarLinhas(raw: any[]): void {
    const itens = raw
      .map((x: any) => this.util.converterParaLowercase(x || {}))
      .map((x: any) => {
        const dataIso = this.normalizarDataIso(x.data);
        return {
          dataIso,
          dataExibicao: this.isoToBr(dataIso),
          codCentroCusto: String(x.codcentrocusto || ''),
          centroCusto: String(x.centrocusto || ''),
          codPlanoContas: String(x.codplanocontas || ''),
          descricaoPlanoContas: String(x.descricaoplanocontas || ''),
          credito: Number(x.credito || 0),
          debito: Number(x.debito || 0),
          saldo: 0,
          informacoesAdicionais: String(x.informacoesadicionais || ''),
          observacao: String(x.observacao || ''),
          mesPagamento: Number(x.mespagamento || 0)
        } as SantanderLinha;
      })
      .filter((x) => !!x.dataIso)
      .sort((a, b) => a.dataIso.localeCompare(b.dataIso));

    let saldo = Number(this.saldoInicialPeriodo || 0);
    let totalCreditos = 0;
    let totalDebitos = 0;
    itens.forEach((linha) => {
      saldo += linha.credito - linha.debito;
      linha.saldo = saldo;
      totalCreditos += linha.credito;
      totalDebitos += linha.debito;
    });

    this.linhas = itens;
    this.totalCreditos = totalCreditos;
    this.totalDebitos = totalDebitos;
    this.saldoAtual = saldo;
    this.paginaAtual = 1;
    this.aplicarOrdenacaoEPaginacao();
  }

  private aplicarOrdenacaoEPaginacao(): void {
    const dir = this.ordenacaoDirecao === 'asc' ? 1 : -1;
    const campo = this.ordenacaoCampo;
    const numerico = this.camposNumericos.includes(campo);

    this.linhasOrdenadas = [...this.linhas].sort((a, b) => {
      if (campo === 'dataIso') {
        return a.dataIso.localeCompare(b.dataIso) * dir;
      }
      if (numerico) {
        const va = Number((a as any)[campo] || 0);
        const vb = Number((b as any)[campo] || 0);
        return (va - vb) * dir;
      }
      const va = String((a as any)[campo] || '').toLowerCase();
      const vb = String((b as any)[campo] || '').toLowerCase();
      return va.localeCompare(vb, 'pt-BR') * dir;
    });

    this.atualizarPaginacao();
  }

  private atualizarPaginacao(): void {
    this.totalPaginas = Math.ceil(this.linhasOrdenadas.length / this.itensPorPagina) || 0;
    if (this.paginaAtual > this.totalPaginas) {
      this.paginaAtual = Math.max(this.totalPaginas, 1);
    }
    if (this.linhasOrdenadas.length === 0) {
      this.linhasPaginadas = [];
      return;
    }
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    this.linhasPaginadas = this.linhasOrdenadas.slice(inicio, inicio + this.itensPorPagina);
  }

  private desempacotar(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual || {});
  }

  private normalizarDataIso(valor: any): string {
    if (!valor) return '';
    if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
      return this.dateToIso(valor);
    }
    const texto = String(valor).trim();
    const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    const data = new Date(texto);
    if (Number.isNaN(data.getTime())) return '';
    return this.dateToIso(data);
  }

  private dateToIso(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private isoToBr(iso: string): string {
    if (!iso || iso.length < 10) return '';
    return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  }
}
